import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Same report presets and server date resolver as StaffReportsPage. No client date arithmetic.
const IT_LABELS = { WEEKLY: 'Settimanale', MONTHLY: 'Mensile', QUARTERLY: 'Trimestrale',
  SEMI_ANNUAL: 'Semestrale', SEASONAL: 'Stagionale', CUSTOM: 'Personalizzato' };

export const useReportPeriod = (api) => {
  const [periodType, setPeriodType] = useState('MONTHLY');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [options, setOptions] = useState([]);
  const [configError, setConfigError] = useState(false);
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    apiRef.current.get('/reports/config', { signal: controller.signal })
      .then(({ data }) => { if (active) setOptions(data.periods); })
      .catch(err => { if (active && err.code !== 'ERR_CANCELED') setConfigError(true); });
    return () => { active = false; controller.abort(); };
  }, []);
  useEffect(() => {
    if (periodType === 'CUSTOM') { setLoading(false); setError(false); return undefined; }
    let active = true;
    const controller = new AbortController();
    setLoading(true); setError(false);
    apiRef.current.get(`/reports/periods?period_type=${periodType}`, { signal: controller.signal })
      .then(({ data }) => { if (active) { setFrom(data.current.from); setTo(data.current.to); } })
      .catch(err => { if (active && err.code !== 'ERR_CANCELED') setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [periodType]);
  return { periodType, from, to, options, loading: loading || (!options.length && !configError), error: error || configError,
    valid: !!from && !!to && from <= to && !loading && !error && !configError && options.some(o => o.value === periodType),
    select: value => { if (value !== periodType) { setLoading(value !== 'CUSTOM'); setPeriodType(value); } },
    setFrom: value => { setPeriodType('CUSTOM'); setFrom(value); },
    setTo: value => { setPeriodType('CUSTOM'); setTo(value); } };
};

export const ReportPeriodSelector = ({ period, t, testId }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 min-w-0 items-end gap-3" data-testid={testId}>
    <div className="min-w-0">
      <label className="text-xs text-zinc-400">{t('Period', 'Periodo')}</label>
      <Select value={period.periodType} onValueChange={period.select}>
        <SelectTrigger data-testid={`${testId}-preset`}><SelectValue /></SelectTrigger>
        <SelectContent>{period.options.map(({ value, label }) => (
          <SelectItem key={value} value={value} data-testid={`${testId}-option-${value.toLowerCase()}`}>{t(label, IT_LABELS[value] || label)}</SelectItem>
        ))}</SelectContent>
      </Select>
    </div>
    <div className="min-w-0">
      <label htmlFor={`${testId}-from`} className="text-xs text-zinc-400">{t('From', 'Dal')}</label>
      <Input id={`${testId}-from`} type="date" value={period.from} onChange={e => period.setFrom(e.target.value)} data-testid={`${testId}-from`} />
    </div>
    <div className="min-w-0">
      <label htmlFor={`${testId}-to`} className="text-xs text-zinc-400">{t('To', 'Al')}</label>
      <Input id={`${testId}-to`} type="date" value={period.to} onChange={e => period.setTo(e.target.value)} data-testid={`${testId}-to`} />
    </div>
    {!period.valid && !period.loading && <p role="alert" className="sm:col-span-3 text-sm text-amber-400" data-testid={`${testId}-error`}>
      {period.error ? t('Unable to load period.', 'Impossibile caricare il periodo.') : t('Select a valid date range.', 'Seleziona un intervallo di date valido.')}
    </p>}
  </div>
);